/* eslint-disable no-nested-ternary */
import { Empty, Skeleton } from 'antd';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import 'highcharts/modules/export-data';
import 'highcharts/modules/exporting';
import 'highcharts/modules/full-screen';
import 'highcharts/modules/offline-exporting';
import _ from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import PropTypes from 'prop-types';
import allActions from '../../../../../../actions';
import * as loadingKeys from '../../../../../../actions/DashboardDomain/LoadingKeys';
import { getConvertedPrice, getCurrency } from '../../../../../../utils/DateAndNumberFunctions';
import { MODES, NUMBER_OF_RECORDS_TO_FETCH } from '../../../../../../utils/GlobalConstants';
import { widgetViewVals } from '../../../../../Config/adminSettings/widgetSetting/WidgetViewConstants';
import styles from '../../styles.module.scss';
import { getDashboardFilters, getMonetaryImpactTot } from '../../utils/WidgetFunctions';

const MonetaryImpact = ({ theme }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const selectedPlants = useSelector((state) => state.commonState.selectedPlants);
  const selectedCCs = useSelector((state) => state.commonState.selectedCompanyCodes);
  const dashboardMode = useSelector((state) => state.commonState.dashboardMode);
  const monetaryImpact = useSelector((state) => state.dashboardState.monetaryImpact);
  const settingsLoaded = useSelector((state) => state.commonState?.loaded?.userSettings);
  const loading = useSelector((state) => state.dashboardState.loading);
  const userSettings = useSelector((state) => state.commonState.userSettings);
  const dashboardFilter = useSelector((state) => state.commonState.localDashboardFilters);
  const currencyList = useSelector((state) => state.commonState?.currencyList);
  const dragStatus = useSelector((state) => state.dashboardState.dragStatus);
  const widgetViews = useSelector((state) => state.commonState.widgetViews);
  const dataSrc = useSelector((state) => state.commonState?.dataSrc?.dashboard);
  const gridData = useSelector((state) => state.dashboardState.gridData);
  const [impact, setImpact] = useState({});
  const [resolvedColors, setResolvedColors] = useState(null);
  const [animation, setAnimation] = useState(false);

  const selectedExists = () => {
    if (selectedCCs?.length === 0 && selectedPlants?.length === 0 && monetaryImpact?.A) {
      return true;
    }
    if (dashboardMode === MODES.PLANT) {
      const key = `P${selectedPlants?.sort().join('')}`;
      return !!monetaryImpact?.[key];
    }
    const key = `CC${selectedCCs?.sort().join('')}`;
    return !!monetaryImpact?.[key];
  };

  const updateCache = () => {
    let tempData;
    const currenctCurrency = userSettings?.DEFAULT_CURRENCY ?? 'NOK';
    let temp = currencyList?.find((x) => currenctCurrency === x?.FROM_CURRENCY);
    if (!temp) temp = [1, 1, 1];
    else temp = [temp.FROM_RATIO, temp.TO_RATIO, temp.EXCHANGE_RATE];
    if (selectedCCs?.length === 0 && selectedPlants?.length === 0) {
      tempData = _.cloneDeep(monetaryImpact.A);
    } else if (dashboardMode === MODES.PLANT) {
      tempData = _.cloneDeep(monetaryImpact[`P${selectedPlants?.sort().join('')}`]);
    } else {
      tempData = _.cloneDeep(monetaryImpact[`CC${selectedCCs?.sort().join('')}`]);
    }
    tempData.data = getMonetaryImpactTot(tempData, dashboardFilter);
    if (!tempData.data?.length) {
      setImpact(tempData);
      return;
    }
    tempData.data[0].Impact = getConvertedPrice(
      tempData.data[0].Impact, ...temp,
    );
    tempData.data[1].Impact = getConvertedPrice(
      tempData.data[1].Impact, ...temp,
    );
    setImpact(tempData);
  };

  useEffect(() => {
    if (selectedPlants && selectedCCs && settingsLoaded && dashboardMode
      && (!!dataSrc)) {
      if (selectedExists()) {
        updateCache();
      } else {
        dispatch(allActions.DashboardActions.setLoading(loadingKeys.monetaryImpact));
        dispatch(allActions.DashboardActions.getMonetaryImpactVals(
          dashboardMode === MODES.PLANT ? selectedPlants : null,
          dashboardMode === MODES.CC ? selectedCCs : null,
          dashboardMode,
          null,
          dataSrc,
        ));
      }
    }
  }, [selectedPlants, selectedCCs, settingsLoaded, dataSrc,
    dashboardMode, monetaryImpact, userSettings, dashboardFilter]);

  const handleClick = (e) => {
    if (e?.label === '' || dragStatus) return;
    const viewID = widgetViews?.find(
      (x) => x.WidgetID === 'monetaryImpact' && (x.GroupID === e.indexValue || x.GroupID === widgetViewVals.all),
    )?.ViewID;
    if (viewID) {
      dispatch(allActions.CommonActions.selectWidgetView(viewID));
    } else {
      dispatch(allActions.CommonActions.selectWidgetView(userSettings?.DEFAULT_USER_VIEW));
    }
    if (e?.id && !e.indexValue) e.indexValue = e.id;
    let searchObject = {
      DISPLAY_REC_IND: [
        {
          ColumnName: 'DISPLAY_REC_IND',
          FilterOperator: 'EqualTo',
          FilterValue: [
            'True',
          ],
        },
      ],
      MONETARY_IMPACT_TYPE: [
        {
          ColumnName: 'MONETARY_IMPACT_TYPE',
          FilterOperator: 'EqualTo',
          FilterValue: [
            e.indexValue,
          ],
        },
      ],
    };
    if (selectedPlants?.length > 0) {
      searchObject = {
        ...searchObject,
        PLANT_FACILITY_SAP_ID: [
          {
            ColumnName: 'PLANT_FACILITY_SAP_ID',
            FilterOperator: 'EqualTo',
            FilterValue:
              selectedPlants,
          },
        ],
      };
    } else if (selectedCCs?.length > 0) {
      searchObject = {
        ...searchObject,
        COMPANY_CODE: [
          {
            ColumnName: 'COMPANY_CODE',
            FilterOperator: 'EqualTo',
            FilterValue:
              selectedCCs,
          },
        ],
      };
    }
    searchObject = {
      ...searchObject,
      ...getDashboardFilters(dashboardFilter),
    };
    const materialViewState = {
      headerCells: [],
      searchFilters: searchObject,
      scrollPage: {
        currentNoOfRecords: 0,
        noOfRecordsToFetch: NUMBER_OF_RECORDS_TO_FETCH,
      },
    };
    if ((selectedPlants?.length > 0
      && !_.isEqual(selectedPlants?.sort(), userSettings?.DEFAULT_PLANTS?.sort()))
      || (selectedCCs?.length > 0
        && !_.isEqual(selectedCCs?.sort(), userSettings?.DEFAULT_COMPANY_CODES?.sort()))) {
      dispatch(allActions.CommonActions.setUseDefaultSettings(false));
    }
    dispatch(allActions.MaterialListActions.setLocalMaterialView(materialViewState));
    dispatch(allActions.MaterialListActions.setSelectedRowKeys([]));
    history.push('/material-list');
  };

  const getColor = (x) => {
    if (resolvedColors) {
      return resolvedColors[x.MONETARY_IMPACT_TYPE] || '#888888';
    }
    switch (x.MONETARY_IMPACT_TYPE) {
      case ('Investment'):
        return 'var(--monetary-impact-negative)';
      case ('Savings'):
        return 'var(--monetary-impact-positive)';
      default: return '#888888';
    }
  };

  const chartComponentRef = useRef(null);
  const [containerNode, setContainerNode] = useState(null);
  const [chartHeight, setChartHeight] = useState(300);

  const containerRef = React.useCallback((node) => {
    setContainerNode(node);
  }, []);

  useEffect(() => {
    if (containerNode) {
      const computedStyle = getComputedStyle(containerNode);
      const newColors = {
        Investment: computedStyle.getPropertyValue('--monetary-impact-negative').trim(),
        Savings: computedStyle.getPropertyValue('--monetary-impact-positive').trim(),
        neutral100: computedStyle.getPropertyValue('--neutral--100').trim(),
        neutral400: computedStyle.getPropertyValue('--neutral--400').trim(),
        neutral600: computedStyle.getPropertyValue('--neutral--600').trim(),
        widgetBg: computedStyle.getPropertyValue('--widget-background').trim(),
      };
      setResolvedColors(newColors);
    }
  }, [containerNode]);

  const createSeries = () => impact?.data?.map((item) => ({
    name: item.MONETARY_IMPACT_TYPE,
    data: [Number(item.Impact || 0)],
    color: getColor(item),
  }));

  useEffect(() => {
    if (!containerNode) return;
    const updateDimensions = () => {
      if (!document.fullscreenElement) {
        const width = containerNode.offsetWidth;
        if (!gridData) {
          setChartHeight((width * 0.33) + 15);
          return;
        }
        const totalHeight = gridData?.gridHeight;
        let finalHeight = totalHeight;
        if (gridData?.colCount === 3) {
          finalHeight = (totalHeight / 3) - 40;
        }
        setChartHeight(finalHeight);
      } else {
        setChartHeight(window.innerHeight);
      }
    };
    updateDimensions();
    const resizeObserver = new ResizeObserver(updateDimensions);
    resizeObserver.observe(containerNode);
    // eslint-disable-next-line consistent-return
    return () => {
      resizeObserver.disconnect();
    };
  }, [containerNode, gridData]);

  const dataLabelsOptions = {
    enabled: true,
    url: '',
    local: true,
    fallbackToExportServer: false,
    y: 0,
    verticalAlign: 'middle',
    align: 'center',
    style: {
      color: resolvedColors?.neutral100 || 'var(--neutral--100)',
      fontWeight: 400,
      textOutline: 'none',
      textStroke: 'none',
      WebkitTextStroke: 'none',
      fontSize: 'max(0.6vw, calc((0.6 * 1.7778) * 1vh))'
    },
    formatter() {
      // eslint-disable-next-line react/no-this-in-sfc
      const { y } = this;
      if (y === 0) return '';
      // eslint-disable-next-line react/no-this-in-sfc
      // eslint-disable-next-line react/no-this-in-sfc
      const absValue = Math.abs(y);
      if (absValue >= 1000000) {
        return `${(absValue / 1000000).toFixed(1)}M`;
      }
      if (absValue >= 1000) {
        return `${(absValue / 1000).toFixed(1)}K`;
      }
      return y.toFixed(0);
    },
  };

  const options = {
    ...theme,
    chart: {
      ...theme.chart,
      type: 'bar',
      height: chartHeight,
      animation: false,
      backgroundColor: resolvedColors?.widgetBg || 'var(--widget-background)',
      load() {
        if (!animation) {
          setAnimation({
            duration: 1200,
            easing: 'easeOutBounce',
            start: {
              x: 0
            }
          });
        }
      },
    },
    title: {
      text: undefined,
    },
    responsive: {
      rules: [{
        condition: {
          maxWidth: 500,
        },
        chartOptions: {
          legend: {
            align: 'center',
            verticalAlign: 'bottom',
            layout: 'horizontal',
          },
          yAxis: {
            labels: {
              align: 'left',
            },
            title: {
              text: null,
            },
          },
        },
      }],
    },
    legend: {
      ...theme.legend,
      enabled: true,
      align: 'center',
      verticalAlign: 'bottom',
      layout: 'horizontal',
      itemStyle: {
        fontSize: 'max(0.6vw, calc((0.6 * 1.7778) * 1vh))',
        color: 'var(--neutral--400)'
      },
      symbolRadius: 0,
      itemHoverStyle: {
        background: 'rgba(0, 0, 0, 0.3)',
        opacity: 1,
      },
    },
    exporting: {
      enabled: true,
      url: '',
      local: true,
      fallbackToExportServer: false,
      error: (_opt, err) => console.log(err),
      menuItemDefinitions: {
        viewData: {
          text: 'View data table'
        }
      },
      buttons: {
        contextButton: {
          menuItems: [
            'viewFullscreen',
            'printChart',
            'downloadPNG',
          ],
          symbol: 'menu',
          symbolX: 3,
          symbolFill: resolvedColors?.neutral100 || 'var(--neutral--100)',
          symbolStroke: resolvedColors?.neutral100 || 'var(--neutral--100)',
          symbolStrokeWidth: 1,
          theme: {
            fill: 'transparent',
            states: {
              hover: {
                fill: 'transparent',
              },
              select: {
                fill: 'transparent',
              }
            }
          }
        }
      },
      chartOptions: {
        chart: {
          marginTop: 10,
          backgroundColor: resolvedColors?.widgetBg || 'var(--widget-background)',
        },
        yAxis: {
          labels: {
            style: {
              color: resolvedColors?.neutral400 || 'var(--neutral--400)',
              fontSize: 'max(0.9vw, calc((0.9 * 1.7778) * 1vh))',
            },
          },
        },
        plotOptions: {
          bar: {
            dataLabels: {
              style: {
                ...dataLabelsOptions.style,
                fontSize: 'max(0.9vw, calc((0.9 * 1.7778) * 1vh))'
              },
            },
          },
        },
        legend: {
          itemStyle: {
            fontSize: 'max(1.5vw, calc((1.5 * 1.7778) * 1vh))',
            color: resolvedColors?.neutral400 || 'var(--neutral--400)'
          },
          symbolHeight: 12,
        }
      }
    },
    navigation: {
      buttonOptions: {
        align: 'right',
        verticalAlign: 'top',
        y: 20, // Move button up slightly
        x: 10, // Move button left slightly
        theme: {
          padding: 8
        }
      },
      menuStyle: {
        background: 'var(--neutral--800)',
        color: 'var(--neutral--100)'
      },
      menuItemStyle: {
        color: 'var(--neutral--100)',
        fontSize: 'max(0.6vw, calc((0.6 * 1.7778) * 1vh))',
        padding: '0.5em 1em',
        transition: 'background 200ms',
      },
      menuItemHoverStyle: {
        background: 'var(--neutral--600)',
        color: 'var(--neutral--100)'
      }
    },
    yAxis: {
      ...theme.yAxis,
      reversedStacks: false,
      opposite: false,
      title: '',
      accessibility: {
        description: '',
      },
      startOnTick: false,
      endOnTick: false,
      labels: {
        ...theme.xAxis?.labels,
        padding: 0,
        distance: 5,
        step: 1,
        useHTML: true,
        allowOverlap: false,
        style: {
          width: 'auto',
          staggerLines: 2,
          margin: 0,
          color: resolvedColors?.neutral400 || 'var(--neutral--400)',
          fontWeight: '400',
          overflow: 'hidden',
          fontSize: 'max(0.6vw, calc((0.6 * 1.7778) * 1vh))',
        },
      }
    },
    xAxis: {
      ...theme.xAxis,
      visible: false,
      title: '',
      accessibility: {
        description: '',
      },
    },
    plotOptions: {
      bar: {
        headSize: 6,
        stacking: 'normal',
        dataLabels: dataLabelsOptions,
        accessibility: {
          exposeAsGroupOnly: true,
        },
        borderWidth: 0,
      },
      series: {
        point: {
          events: {
            click: (event) => {
              const { point } = event;
              const pointData = {
                indexValue: point.series.name,
                seriesName: point.series.name,
                y: point.y,
                color: point.color,
              };
              handleClick(pointData);
            },
          },
        },
        animation: false
      }
    },
    colors: theme.colors,
    series: createSeries(),
    credits: {
      enabled: false,
    },
    tooltip: {
      ...theme.tooltip,
      followPointer: true,
      useHTML: true,
      formatter() {
        const { point, series } = this;
        const impactType = series.name;
        const value = point.y;
        const currency = getCurrency(true, userSettings?.DEFAULT_CURRENCY);

        // Format the value similar to dataLabels
        let formattedValue;
        if (value === 0) {
          formattedValue = '0';
        } else {
          const absValue = Math.abs(value);
          if (absValue >= 1000000) {
            formattedValue = `${(absValue / 1000000).toFixed(1)}M`;
          } else if (absValue >= 1000) {
            formattedValue = `${(absValue / 1000).toFixed(1)}K`;
          } else {
            formattedValue = `${value.toFixed(0)}`;
          }
        }

        return `<div style="padding: 8px">
          <strong>${impactType}:</strong> ${formattedValue} ${currency}
        </div>`;
      },
    },
  };

  return (
    <div className={styles.widgetHeight}>
      {
        impact?.data
          && !loading?.[loadingKeys?.monetaryImpact]
          && ((dashboardMode === MODES.PLANT && _.isEqual(impact?.plants, selectedPlants))
            || (dashboardMode === MODES.CC && _.isEqual(impact?.companyCodes, selectedCCs)))
          ? (
            impact?.data[0]
              ? (
                <div ref={containerRef} className={styles.chartContainer}>
                  <HighchartsReact
                    highcharts={Highcharts}
                    options={{
                      ...options,
                    }}
                    ref={chartComponentRef}
                  />
                </div>
              ) : <Empty description={_.isEmpty(dashboardFilter?.filterVals) ? 'No data' : 'No data after filtering'} />
          )
          : (
            <Skeleton
              active
              paragraph={{ rows: 2 }}
            />
          )
      }
    </div>
  );
};
export default MonetaryImpact;
MonetaryImpact.defaultProps = {};

MonetaryImpact.propTypes = {
  theme: PropTypes.objectOf(PropTypes.any).isRequired,
};
